import React, { useState, useEffect, useRef } from 'react';
import './Chatbot.css';
import logo from '../assets/Zenio+Logo+header.png';
import { api, useLocalStorageFallback } from '../services/api';

function Chatbot({ user, onBack }) {
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: 'Hello! I can help you find employee information. Try asking me things like:\n• "Who is John Smith?"\n• "What\'s the contact info for Sarah?"\n• "Show me all employees in Engineering"\n• "Find employees"\n• "Help"',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const useFallback = useLocalStorageFallback();

  // Load employees on mount
  useEffect(() => {
    loadEmployees();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadEmployees = async () => {
    // Always try localStorage first as a quick load
    const saved = localStorage.getItem('zenio_employees');
    if (saved) {
      try {
        setEmployees(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse saved employees:', e);
      }
    }

    // Then try the API to get fresh data
    if (!useFallback) {
      try {
        const result = await api.getEmployees();
        if (result && result.success && result.employees) {
          setEmployees(result.employees);
        } else if (result && Array.isArray(result)) {
          // Handle case where API returns array directly
          setEmployees(result);
        }
      } catch (error) {
        console.error('Failed to load employees from API, using localStorage fallback:', error);
        // Already loaded from localStorage above, so this is fine
      }
    }
  };

  const addMessage = (type, text) => {
    setMessages(prev => [...prev, {
      type,
      text,
      timestamp: new Date()
    }]);
  };

  const searchEmployees = (query) => {
    const lowerQuery = query.toLowerCase();
    
    // If asking for all employees - check this FIRST
    if (lowerQuery.includes('all employees') || 
        lowerQuery.includes('list employees') || 
        lowerQuery === 'list all employees' ||
        lowerQuery === 'list all' ||
        lowerQuery === 'show all') {
      return employees;
    }
    
    // Check if this looks like a department query pattern
    const deptPatterns = [
      /(?:show|list|find|get)\s+(?:me\s+)?employees?\s+(?:in|from)\s+(.+)/i,
      /(?:employees?\s+(?:in|from)\s+)(.+)/i
    ];
    
    let extractedDept = null;
    for (const pattern of deptPatterns) {
      const match = query.match(pattern);
      if (match && match[1]) {
        extractedDept = match[1].trim();
        break;
      }
    }
    
    // If we detected a department query pattern and it's not a generic word
    const genericWords = ['all', 'every', 'everyone', 'everything'];
    if (extractedDept && !genericWords.includes(extractedDept.toLowerCase())) {
      // Try to find the exact department name (case-insensitive)
      const departments = [...new Set(employees.map(e => e.department).filter(Boolean))];
      const matchedDept = departments.find(dept => 
        dept.toLowerCase() === extractedDept.toLowerCase() || 
        extractedDept.toLowerCase().includes(dept.toLowerCase())
      );
      
      if (matchedDept) {
        return { results: employees.filter(e => e.department === matchedDept), department: matchedDept };
      } else {
        // Department doesn't exist, but we still return the structure
        return { results: [], department: extractedDept };
      }
    }
    
    // Check for department queries by matching existing department names
    const departments = [...new Set(employees.map(e => e.department).filter(Boolean))];
    const mentionedDept = departments.find(dept => lowerQuery.includes(dept.toLowerCase()));

    if (mentionedDept) {
      return { results: employees.filter(e => e.department === mentionedDept), department: mentionedDept };
    }

    // Extract potential name from query
    const words = lowerQuery.split(' ').filter(w => 
      w.length > 2 && 
      !['who', 'what', 'where', 'when', 'how', 'the', 'is', 'are', 'for', 'show', 'find', 'get', 'me', 'all', 'contact', 'info', 'information', 'details', 'about'].includes(w)
    );

    // Search by name
    if (words.length > 0) {
      return employees.filter(emp => {
        const empName = emp.name.toLowerCase();
        return words.some(word => empName.includes(word));
      });
    }

    return [];
  };

  const formatEmployeeInfo = (emp) => {
    let info = `📋 **${emp.name}**\n`;
    if (emp.title) info += `\n💼 Title: ${emp.title}`;
    if (emp.department) info += `\n🏢 Department: ${emp.department}`;
    if (emp.email) info += `\n📧 Email: ${emp.email}`;
    if (emp.phone) info += `\n📞 Phone: ${emp.phone}`;
    if (emp.extension) info += `\n☎️ Extension: ${emp.extension}`;
    return info;
  };

  const processQuery = async (query) => {
    const lowerQuery = query.toLowerCase();

    // Help command
    if (lowerQuery.includes('help') || lowerQuery === '?') {
      return `I can help you with:\n\n📋 Employee Information:\n• "Who is [name]?"\n• "Contact info for [name]"\n• "Find [name]"\n• "Show me employees in [department]"\n• "List all employees"\n\n💡 Just ask naturally and I'll do my best to help!`;
    }

    // Employee search
    if (lowerQuery.includes('employee') || 
        lowerQuery.includes('who is') ||
        lowerQuery.includes('contact') ||
        lowerQuery.includes('find') ||
        lowerQuery.includes('show') ||
        lowerQuery.includes('list') ||
        lowerQuery.includes('email') ||
        lowerQuery.includes('phone')) {
      
      const results = searchEmployees(query);

      // Handle department search response
      if (results && results.department) {
        if (results.results.length === 0) {
          return `There are no employees on record with ${results.department} department`;
        }
        const actualResults = results.results;
        if (actualResults.length === 1) {
          return formatEmployeeInfo(actualResults[0]);
        }
        if (actualResults.length <= 5) {
          return `I found ${actualResults.length} employees in ${results.department}:\n\n` + 
                 actualResults.map(emp => formatEmployeeInfo(emp)).join('\n\n---\n\n');
        }
        return `I found ${actualResults.length} employees in ${results.department}. Here are the first 5:\n\n` +
               actualResults.slice(0, 5).map(emp => formatEmployeeInfo(emp)).join('\n\n---\n\n') +
               `\n\n(${actualResults.length - 5} more employees in this department)`;
      }

      // Check if this was a "list all" type query
      const isListAll = lowerQuery.includes('all employees') || 
                        lowerQuery.includes('list employees') || 
                        lowerQuery === 'list all employees' ||
                        lowerQuery === 'list all' ||
                        lowerQuery === 'show all';

      // Handle regular search results (name search or list all)
      if (results.length === 0) {
        if (isListAll) {
          return `No employees are currently on record. Employees can be added through the Employee Directory.`;
        }
        return `I couldn't find any employees matching \"${query}\". Try:\n• Using a different name\n• Asking \"List all employees\" to see everyone`;
      }

      if (results.length === 1) {
        return formatEmployeeInfo(results[0]);
      }

      if (isListAll) {
        // For "list all" queries, show up to 10 and indicate total
        if (results.length <= 10) {
          return `Here are all ${results.length} employees on record:\n\n` + 
                 results.map(emp => formatEmployeeInfo(emp)).join('\n\n---\n\n');
        }
        return `There are ${results.length} employees on record. Here are the first 10:\n\n` +
               results.slice(0, 10).map(emp => formatEmployeeInfo(emp)).join('\n\n---\n\n') +
               `\n\n(${results.length - 10} more employees on record)`;
      }

      if (results.length <= 5) {
        return `I found ${results.length} employees:\n\n` + 
               results.map(emp => formatEmployeeInfo(emp)).join('\n\n---\n\n');
      }

      // Too many results
      return `I found ${results.length} employees. Here are the first 5:\n\n` +
             results.slice(0, 5).map(emp => formatEmployeeInfo(emp)).join('\n\n---\n\n') +
             `\n\n(${results.length - 5} more employees match your search)`;
    }

    // Default response
    return `I'm not sure how to help with that. Try asking about employees or type "help" for more options.`;
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput('');
    addMessage('user', userMessage);
    setIsLoading(true);

    // Simulate thinking delay
    setTimeout(async () => {
      const response = await processQuery(userMessage);
      addMessage('bot', response);
      setIsLoading(false);
    }, 500);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
    // Auto-send the suggestion
    setTimeout(() => {
      const userMessage = suggestion;
      setInput('');
      addMessage('user', userMessage);
      setIsLoading(true);

      setTimeout(async () => {
        const response = await processQuery(userMessage);
        addMessage('bot', response);
        setIsLoading(false);
      }, 500);
    }, 100);
  };

  const suggestions = [
    "List all employees",
    "Show employees in Engineering",
    "Show employees in Sales",
    "Help"
  ];

  return (
    <div className="chatbot-page">
      <div className="chatbot-header">
        <button className="back-button" onClick={onBack}>
          ← Back to Dashboard
        </button>
        <div className="header-logo-section">
          <div className="header-logo-backdrop">
            <img src={logo} alt="ZenIO Logo" className="header-logo-img" />
          </div>
          <h1>ZenIO Support (FOR DEMO USE ONLY)</h1>
        </div>
      </div>

      <div className="chatbot-container">
        <div className="chat-messages">
          {messages.map((msg, index) => (
            <div key={index} className={`message ${msg.type}`}>
              <div className="message-avatar">
                {msg.type === 'bot' ? '🤖' : '👤'}
              </div>
              <div className="message-content">
                <div className="message-text">
                  {msg.text.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line.includes('**') ? (
                        <strong>{line.replace(/\*\*/g, '')}</strong>
                      ) : line.startsWith('•') ? (
                        <div className="bullet-point">{line}</div>
                      ) : (
                        <span>{line}</span>
                      )}
                      {i < msg.text.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
                <div className="message-time">
                  {msg.timestamp.toLocaleTimeString('en-US', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    timeZone: 'America/Los_Angeles'
                  })}
                </div>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="message bot">
              <div className="message-avatar">🤖</div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="suggestion-buttons">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              className="suggestion-btn"
              onClick={() => handleSuggestionClick(suggestion)}
              disabled={isLoading}
            >
              {suggestion}
            </button>
          ))}
        </div>

        <div className="chat-input-area">
          <input
            type="text"
            className="chat-input"
            placeholder="Ask me about employees..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
          />
          <button 
            className="send-button" 
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
          >
            Send
          </button>
        </div>

        <div className="chatbot-footer">
          <small>💡 Tip: Ask naturally! Example: "Who is John Smith?" or "Find employees in Engineering"</small>
        </div>
      </div>
    </div>
  );
}

export default Chatbot;
